/**
 * Route Helpers for Fulfillment Lifecycle Dashboard
 *
 * Categorization logic for grouping routes into status sections.
 */

import { getPullTimeDelta } from './formatters';

/**
 * Default threshold (in minutes) for considering a pull time "soon".
 * Routes with pull time within this window (or already past) are considered active.
 */
const DEFAULT_SOON_THRESHOLD_MINUTES = 120;

/**
 * Determine if a route's pull time is "soon" — within threshold or already past.
 *
 * @param {Object} route - Route object with pullTime
 * @param {Date} now - Current time
 * @param {number} [thresholdMinutes=120] - Minutes before pull time to consider "soon"
 * @returns {boolean} True if pull time is within threshold or past
 */
export const isPullTimeSoon = (route, now, thresholdMinutes = DEFAULT_SOON_THRESHOLD_MINUTES) => {
    const delta = getPullTimeDelta(route.pullTime, now);
    // No pull time available — treat as active to surface it
    if (delta === null) {
        return true;
    }
    // delta > 0 means pull time is past; delta > -threshold means within window
    return delta > -thresholdMinutes;
};

/**
 * Categorize routes into status sections for the dashboard grid.
 *
 * Sections:
 * - active: Not fully shipped AND pull time is soon/past
 * - upcoming: Not fully shipped AND pull time is far away
 * - complete: All shipments in transit (totalShipments > 0 && transitCount >= totalShipments)
 * - unrouted: Reserved for future use (routes without line haul assignment)
 *
 * @param {Object[]} routes - Array of route objects
 * @param {Date} now - Current time for pull time calculations
 * @returns {{ active: Object[], upcoming: Object[], complete: Object[], unrouted: Object[] }}
 */
export const categorizeRoutes = (routes, now) => {
    const active = [];
    const upcoming = [];
    const complete = [];

    routes.forEach((route) => {
        if (route.totalShipments > 0 && route.transitCount >= route.totalShipments) {
            complete.push(route);
        } else if (isPullTimeSoon(route, now)) {
            active.push(route);
        } else {
            upcoming.push(route);
        }
    });

    return {
        active,
        upcoming,
        complete,
        unrouted: [], // Future: routes without line_haul_id
    };
};
